//******************************************************************************
// import
// 測地線と方形線の計算
import { Geodesic } from "geographiclib-geodesic";
// 地理座標を地心座標に変換する際に使用
import { grs80, latLngToCartesian } from "../Projection/projection";

//******************************************************************************
// WGS84ではなくGRS80を使用する
const geod = new Geodesic.Geodesic(grs80.a, grs80.f);

//******************************************************************************
// ユーティリティクラス化
export class MeasureUtils {
	//**************************************************************************
	/**
	 * 経緯度標高を内部用のオブジェクトに整理
	 * @param {Array|Object} lnglatElev
	 * @returns {{lng:number, lat:number, elev:number}}
	*/
	optimize(lnglatElev) {
		//----------------------------------------------------------------------
		// 経度を処理する
		const lng = Array.isArray(lnglatElev)
			? lnglatElev[0]
			: lnglatElev.lng ?? lnglatElev.lon ?? lnglatElev.longitude ?? false;
		//----------------------------------------------------------------------
		// 緯度を処理する
		const lat = Array.isArray(lnglatElev)
			? lnglatElev[1]
			: lnglatElev.lat ?? lnglatElev.latitude ?? false;
		//----------------------------------------------------------------------
		// 標高を処理する
		const elev = Array.isArray(lnglatElev)
			? lnglatElev[2]
			: lnglatElev.elev ?? lnglatElev.elevation ?? lnglatElev.alt ?? lnglatElev.altitude ?? lnglatElev.height ?? 0;
		//----------------------------------------------------------------------
		// バリデーション
		[lng, lat, elev].forEach((value) => {
			if (Number.isNaN(value) === true) {
				throw new Error("Unxpected value is specified.");
			}
		});
		//----------------------------------------------------------------------
		// 結果を返す
		return { lng, lat, elev };
	}

	//**************************************************************************
	/**
	 * ラジアンを度に変換
	 * @param {number} value
	 * @returns {number}
	*/
	radianToDegree(value) {
		return value / Math.PI * 180;
	}

	//**************************************************************************
	/**
	 * 度をラジアンに変換
	 * @param {number} value
	 * @returns {number}
	*/
	degreeToRadian(value) {
		return value * Math.PI / 180;
	}

	//**************************************************************************
	/**
	 * 2点間の直線距離（測地線距離ではない）を求める（ジオイド高は考慮されていない）
	 * @param {Array|Object} srcLngLatElev
	 * @param {Array|Object} dstLngLatElev
	 * @returns {number}
	*/
	getDistance(srcLngLatElev, dstLngLatElev) {
		//----------------------------------------------------------------------
		// 引数を整理して地心座標に変換
		const points = [
			this.optimize(srcLngLatElev),
			this.optimize(dstLngLatElev)
		].map((coordinates) => {
			return latLngToCartesian({
				lng: coordinates.lng,
				lat: coordinates.lat,
			}, coordinates.elev);
		});
		//----------------------------------------------------------------------
		// 三平方の定理で距離を求めて返す
		return Math.sqrt(["x", "y", "z"].reduce((acc, cur) => {
			return acc + (points[1][cur] - points[0][cur]) ** 2;
		}, 0));
	}

	//**************************************************************************
	//出発地から目的地の方位角を求める
	//地理院ウェブサイトでの計算結果と異なるのは回転楕円体ではなく球面として扱っているからだと思う
	//カシオの計算サイトと同じアルゴリズム
	//https://keisan.casio.jp/exec/system/1257670779
	//著名なアルゴリズムの論文あり（Charles F. F. Karney, 2012: Algorithms for geodesics）
	//https://link.springer.com/content/pdf/10.1007/s00190-012-0578-z.pdf
	//上記論文を実装した各種言語版ライブラリがsourceForgeにあり（npmパッケージもあり）
	//https://geographiclib.sourceforge.io
	/*
		@param	mixed	: array or object of geographic coordinates of source
		@param	mixed	: array or object of geographic coordinates of destination
		@return	number	: direction from source to destination
	*/
	// _getDirection ( srcLngLat, dstLngLat ) {
	// 	//----------------------------------------------------------------------
	// 	//引数を整理してラジアン値の経緯度に変換
	// 	const points	= [
	// 		this.optimize( dstLngLat ),
	// 		this.optimize( srcLngLat ),
	// 	].map(( value ) => {
	// 		return {
	// 			lat: this.degreeToRadian( value.lat, ),
	// 			lng: this.degreeToRadian( value.lng, ),
	// 		}
	// 	});
	// 	//----------------------------------------------------------------------
	// 	//出発地から目的地への方位角を計算
	// 	const dLng	= points[0].lng - points[1].lng;
	// 	const x	= Math.sin( dLng ) * Math.cos( points[0].lat );
	// 	const y	= Math.cos( points[1].lat ) * Math.sin( points[0].lat )
	// 	- Math.sin( points[1].lat ) * Math.cos( points[0].lat ) * Math.cos( dLng );
	// 	const radian	= Math.atan2( x, y );
	// 	//----------------------------------------------------------------------
	// 	//度に変換
	// 	const degree	= this.radianToDegree( radian );
	// 	//----------------------------------------------------------------------
	// 	//マイナス値はプラス値に変換して返す
	// 	return degree + ( degree < 0 ? 360 : 0 );
	// }

	//**************************************************************************
	/**
	 * 出発地から目的地の方位角を求める
	 * @param {Array|Object} srcLngLatElev
	 * @param {Array|Object} dstLngLatElev
	 * @returns {number}
	 */
	getDirection(srcLngLat, dstLngLat) {
		//----------------------------------------------------------------------
		// 引数を整理する
		const points = [
			this.optimize(srcLngLat),
			this.optimize(dstLngLat),
		];
		//----------------------------------------------------------------------
		// 演算する（方位なので標高は考慮しない）
		const result = geod.Inverse(
			points[0].lat, points[0].lng, points[1].lat, points[1].lng
		);
		/*
			Invertの結果で得られるオブジェクトの意味
			a12		: 測地線（2点を結ぶ最短経路）の正角距離（角度単位）。単位は度。
			azi1	: 始点から終点に向かう測地線の方位角。北を基準に時計回りで計測。単位は度。範囲-180から180。
			azi2	: 終点での測地線の方位角。始点と終点での方位角が異なのは地球の曲率や測地線の性質による。単位は度。範囲-180から180。
			lat1	: 始点の緯度。単位は度。
			lat2	: 終点の緯度。単位は度。
			lon1	: 始点の経度。単位は度。
			lon2	: 終点の経度。単位は度。
			s12		: 始点から終点までの測地線の長さ（地表上の実際の距離）。単位はメートル。
		*/
		//----------------------------------------------------------------------
		// 結果を返す（マイナスはプラスにする）
		return result.azi1 + (result.azi1 < 0 ? 360 : 0);
	}

	//**************************************************************************
	/**
	 * 3点から角度を求める
	 * @param {Array|Object} lnglatElev
	 * @param {Array<Array|Object>} lnglatElevs
	 * @returns {number}
	*/
	getAngle(lnglatElev, lnglatElevs) {
		//----------------------------------------------------------------------
		// 頂角の地心座標を求める
		const lnglatElevOrigin = this.optimize(lnglatElev);
		const origin = latLngToCartesian({
			lat: lnglatElevOrigin.lat,
			lng: lnglatElevOrigin.lng,
		}, lnglatElevOrigin.elev);
		//----------------------------------------------------------------------
		// 底角の地心座標を求める
		const sides = lnglatElevs.map((value) => {
			const lnglatElev = this.optimize(value);
			return latLngToCartesian({
				lat: lnglatElev.lat,
				lng: lnglatElev.lng,
			}, lnglatElev.elev);
		});
		//----------------------------------------------------------------------
		// 頂角から底角へのベクトルを求める
		const vectors = sides.map((value) => {
			return {
				x: origin.x - value.x,
				y: origin.y - value.y,
				z: origin.z - value.z,
			}
		});
		//----------------------------------------------------------------------
		// ベクトルの内積を求める
		const scalar = ["x", "y", "z"].reduce((acc, cur) => {
			return acc + vectors[0][cur] * vectors[1][cur];
		}, 0);
		//----------------------------------------------------------------------
		// ベクトルの大きさ（norm）を求める
		const norm = vectors.reduce((acc, cur) => {
			return acc * Math.sqrt(cur.x ** 2 + cur.y ** 2 + cur.z ** 2);
		}, 1);
		//----------------------------------------------------------------------
		// ベクトルの大きさのバリデーション
		if (norm === 0) {
			throw new Error("Same points selected or calculation error.");
		}
		//----------------------------------------------------------------------
		// cos値を決める
		const cosine = scalar / norm;
		//----------------------------------------------------------------------
		// 結果を度にして返す（計算誤差で範囲外になる可能性があるのでその対応をしておく）
		return this.radianToDegree(
			Math.acos(Math.min(Math.max(cosine, -1), 1))
		);
	}

	//**************************************************************************
	/**
	 * 2点の傾斜を求める
	 * @param {Array|Object} srcLngLatElev
	 * @param {Array|Object} dstLngLatElev
	 * @returns {number}
	*/
	getInclination(srcLngLatElev, dstLngLatElev) {
		//----------------------------------------------------------------------
		// 引数を整理
		const optimized = [
			this.optimize(srcLngLatElev),
			this.optimize(dstLngLatElev)
		];
		//----------------------------------------------------------------------
		// 直線距離
		const distance = this.getDistance(srcLngLatElev, dstLngLatElev);
		//----------------------------------------------------------------------
		// 標高差
		const vertical = optimized[1].elev - optimized[0].elev;
		//----------------------------------------------------------------------
		// 結果を返す
		return this.radianToDegree(
			Math.asin(vertical / distance)
		);
	}
}
